import React, { useState, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { noAutofillProps } from '../../utils/noAutofill';

const VenueAutocomplete = ({
  value,
  onChange,
  placeholder,
  className,
  onPlaceSelect
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const searchVenues = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${getApiBase()}/places/autocomplete`, {
        params: {
          query: query
        },
        headers: getAuthHeaders()
      });

      if (response.data.status === 'OK' && response.data.predictions) {
        setSuggestions(response.data.predictions.slice(0, 8));
      } else if (response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
      } else {
        console.warn('Google Places API returned status:', response.data.status);
        setError('Unable to fetch location suggestions');
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Error fetching venue suggestions:', err);
      if (err.response?.status === 503) {
        setError('Google Places API is not configured. Contact your administrator.');
        console.error('Google Places API key not configured. See QUICKSTART_FIX.md for setup instructions.');
      } else {
        setError('Failed to fetch location suggestions');
      }
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setShowSuggestions(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchVenues(inputValue);
    }, 300);
  };

  const handleSelectVenue = async (prediction) => {
    // For address results, show the full description (e.g., "1505 Bellas Vista Dr, Dallas, TX")
    // For establishment results, show just the name (e.g., "Marriott Hotel")
    const types = prediction.types || [];
    const isAddress = types.includes('street_address') || types.includes('route') || types.includes('premise') || types.includes('subpremise') || types.includes('geocode');
    const displayValue = isAddress
      ? (prediction.description || prediction.structured_formatting?.main_text)
      : (prediction.structured_formatting?.main_text || prediction.description);
    onChange(displayValue);
    setShowSuggestions(false);
    setSuggestions([]);

    // If onPlaceSelect is provided, fetch detailed place information
    if (onPlaceSelect) {
      try {
        const response = await axios.get(`${getApiBase()}/places/details`, {
          params: {
            place_id: prediction.place_id
          },
          headers: getAuthHeaders()
        });

        if (response.data.status === 'OK' && response.data.result) {
          const place = response.data.result;
          onPlaceSelect({
            place_id: place.place_id,
            name: place.name,
            formatted_address: place.formatted_address,
            geometry: place.geometry,
            types: place.types || [],
            rating: place.rating,
            phone: place.formatted_phone_number,
            website: place.website
          });
        }
      } catch (err) {
        console.error('Error fetching place details:', err);
        // Still call onPlaceSelect with basic info from prediction
        onPlaceSelect({
          place_id: prediction.place_id,
          name: prediction.structured_formatting?.main_text || '',
          formatted_address: prediction.description,
          types: prediction.types || []
        });
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    if (value && value.length >= 2) {
      searchVenues(value);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        {...noAutofillProps}
        name="venue-search"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
      />

      {error && !loading && (
        <div className="absolute z-10 w-full bg-ui-amber-bg border border-ui-amber-line rounded-md shadow-lg mt-1 p-2">
          <div className="text-xs text-ui-amber-fg">
            ⚠️ {error}
          </div>
        </div>
      )}

      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div className="absolute z-10 w-full bg-ui-surface border border-ui-control rounded-md shadow-lg max-h-64 overflow-y-auto mt-1">
          {loading && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              🔍 Searching locations...
            </div>
          )}

          {!loading && suggestions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectVenue(prediction)}
              className="w-full text-left px-3 py-2 hover:bg-ui-hover focus:bg-ui-hover focus:outline-none border-b border-ui-divider last:border-b-0"
            >
              <div className="font-medium text-ui-ink">
                {prediction.structured_formatting?.main_text}
              </div>
              <div className="text-sm text-ui-muted">
                {prediction.structured_formatting?.secondary_text}
              </div>
            </button>
          ))}

          {!loading && suggestions.length === 0 && value && value.length >= 2 && !error && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              No locations found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VenueAutocomplete;
